import type { FounderCategoryCode, InviteLookupResponse, MeUpdateResponse, UserRole } from '@doneverse/shared'
import { api } from '../api/index.js'
import { getAuth0PasswordResetConfig } from '../api/index.js'
import { isNotFoundError } from '../api/index.js'

interface ProfileUpdates {
  name?: string | null
  founder_category?: FounderCategoryCode | null
}

export const authService = {
  /**
   * Update user profile via platform API
   * - `name` is sent directly to the API
   * - `founder_category` is NOT supported for self-updates (requires admin)
   */
  updateProfile: async (
    _userId: string,
    updates: ProfileUpdates,
  ): Promise<{ data: MeUpdateResponse | null; error: Error | null }> => {
    try {
      // Only name updates are supported via /v1/me
      // founder_category requires admin action via /v1/admin/users/:id
      if (updates.founder_category !== undefined) {
        console.warn('founder_category self-updates are not supported. Use admin API instead.')
      }

      const payload: { name?: string } = {}
      if (updates.name !== undefined) {
        payload.name = updates.name ?? ''
      }

      // If only founder_category was provided (no name), return success without API call
      if (Object.keys(payload).length === 0) {
        return { data: null, error: null }
      }

      const data = await api.patch(payload, '/v1/me').json<MeUpdateResponse>()
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error instanceof Error ? error : new Error('Update failed') }
    }
  },

  inviteUser: async (body: { email: string; name: string; role: UserRole; skip_email?: boolean }) => {
    return await api.post(body, '/v1/admin/users/invite').json()
  },

  lookupInvite: async (inviteToken: string): Promise<InviteLookupResponse> => {
    try {
      return await api.get(`/v1/invites/${encodeURIComponent(inviteToken)}`).json<InviteLookupResponse>()
    } catch (error) {
      if (isNotFoundError(error)) {
        throw new Error('Invite link is invalid or has expired')
      }
      throw error instanceof Error ? error : new Error('Failed to load invite details')
    }
  },

  /**
   * Trigger an Auth0 password reset email. Auth0 config is read from the
   * platform-injected getter so the service is free of web-only
   * `import.meta.env` reads and works in React Native too.
   */
  requestPasswordResetAuth0: async (email: string) => {
    const { domain, clientId } = getAuth0PasswordResetConfig()
    const connection = getAuth0PasswordResetConfig().connection || 'Username-Password-Authentication'

    if (!domain || !clientId) {
      throw new Error('Auth0 is not configured for password reset')
    }

    const response = await fetch(`https://${domain}/dbconnections/change_password`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        email,
        connection,
      }),
    })

    if (!response.ok) {
      const message = await response.text().catch(() => '')
      throw new Error(message || 'Request failed')
    }

    return true
  },
}
